import { TextVariant } from "@/types/text-variants";
import { useDisclosure } from "@mantine/hooks";
import DOMPurify from "dompurify";
import { IoIosSend, IoMdCheckmark } from "react-icons/io";
import { IoCheckmarkDoneOutline } from "react-icons/io5";
import { BiError } from "react-icons/bi";
import { channelColors, channelIcons } from "@/constants/channelIcons";
import { api } from "@/lib/api-client";
import { LimeModal } from "@/Components/NextBase/LimeModal";
import { Text } from "@mantine/core";
import {
  Badge,
  Chip,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
  Snippet,
} from "@heroui/react";
import { Trans } from "@lingui/react/macro";
import { getLocalTimeZone } from "@internationalized/date";
import { i18n } from "@lingui/core";
import dayjs from "dayjs";
import { Mail, MailOpen, MailX } from "lucide-react";

type NotificationLog = {
  trigger: string;
  message_content: string;
  timeSent: string;
  status: "success" | "error" | "PENDING_ACCEPTED";
  deliveryStatus: "pending" | "delivered" | "seen" | "error";
  notificationType: "sms" | "viber" | "whatsapp" | "email";
  originalType: "sms" | "viber" | "whatsapp" | "email";
  notificationLogId: number;
  emailStatus:
    | "stored"
    | "complained"
    | "unsubscribed"
    | "clicked"
    | "opened"
    | "failed"
    | "delivered"
    | "rejected"
    | "accepted";
};

const statusIcons = (
  deliveryStatus: NotificationLog["deliveryStatus"],
  type: NotificationLog["originalType"],
) => {
  const color = channelColors[type];

  if (deliveryStatus === "pending") {
    return <IoIosSend size={20} color={"gray"} />;
  } else if (deliveryStatus === "delivered") {
    return <IoMdCheckmark size={20} color={"gray"} />;
  } else if (deliveryStatus === "seen") {
    return <IoCheckmarkDoneOutline size={20} color={color} />;
  } else if (deliveryStatus === "error") {
    return <BiError size={20} color={"red"} />;
  }
};

const emailStatusIcons = (eventType: NotificationLog["emailStatus"]) => {
  switch (eventType) {
    case "opened":
      return <MailOpen size={20} color={"gray"} />;
    case "delivered":
      return <Mail size={20} color={"gray"} />;
    case "accepted":
      return <IoIosSend size={20} color={"gray"} />;
    case "failed":
      return <MailX size={20} color={"gray"} />;
    default:
      return <IoIosSend size={20} color={"gray"} />;
  }
};

export const NotificationTriggerSection = ({
  triggers,
  notificationLog,
  label,
}: {
  triggers: string[];
  notificationLog: NotificationLog[];
  label: string;
}) => {
  if (
    !notificationLog ||
    !notificationLog.find((nl) => triggers.includes(nl.trigger))
  ) {
    return null;
  }

  return (
    <>
      <Text variant={TextVariant.BodyEmphasized}>{label}</Text>
      {notificationLog
        .filter((nl) => triggers.includes(nl.trigger))
        .map((notification) => {
          return <NotificationPreviewRow notification={notification} />;
        })}
    </>
  );
};

const NotificationPreviewRow = ({
  notification,
}: {
  notification: NotificationLog;
}) => {
  const [opened, { open, close }] = useDisclosure(false);

  const notificationType =
    notification.notificationType || notification.originalType;

  return (
    <>
      <button
        type="button"
        className="flex w-full cursor-pointer items-center justify-between p-1 hover:bg-gray-100"
        onClick={open}
      >
        <Text variant={TextVariant.Body}>{channelIcons[notificationType]}</Text>

        <div className="flex items-center gap-2">
          <Text variant={TextVariant.Body}>{notification.timeSent}</Text>
          <div>
            {statusIcons(notification.deliveryStatus, notificationType)}
          </div>
        </div>
      </button>

      <LimeModal
        size="xl"
        isOpen={opened}
        onClose={close}
        title={`${notificationType} - ${notification.timeSent}`}
      >
        <ModalContent>
          <ModalHeader>
            <Trans>Podrobnosti obvestila</Trans>
          </ModalHeader>
          <ModalBody>
            <NotificationActivityPreview
              notificationLogId={notification.notificationLogId}
            ></NotificationActivityPreview>
          </ModalBody>
          <ModalFooter></ModalFooter>
        </ModalContent>
      </LimeModal>
    </>
  );
};

const NotificationActivityPreview = ({
  notificationLogId,
}: {
  notificationLogId: number;
}) => {
  const { data } =
    api.notification.useGetNotificationLogDetails(notificationLogId);

  console.log(data);

  if (!data) {
    return null;
  }

  const startContent = (() => {
    const jsxContent = (() => {
      const content = data.notificationLog.message_content || "";
      if (
        data.notificationLog.notificationType === "email" &&
        content.indexOf("<html") >= 0
      ) {
        const sanitizedContent = DOMPurify.sanitize(content);
        return (
          <div
            dangerouslySetInnerHTML={{
              __html: sanitizedContent,
            }}
          ></div>
        );
      } else {
        return <Text c={"black"}>{content}</Text>;
      }
    })();

    const deliveryEvent = (() => {
      return (
        data.notificationLog.fallbackNotificationActivity.find((activity) =>
          ["delivered", "seen"].includes(activity.deliveryStatus),
        ) ||
        data.notificationLog.emailNotificationActivity.find((activity) =>
          ["delivered", "opened"].includes(activity.eventType),
        )
      );
    })();

    return (
      <div className="space-y-3">
        <div className="grid grid-cols-1 gap-2">
          <div className="flex justify-between">
            <span className="text-sm text-foreground-500">Sent at:</span>
            <span className="text-sm font-medium">
              {new Date(data.notificationLog.timeSent).toLocaleString(
                i18n.locale,
                {
                  timeZone: getLocalTimeZone(),
                },
              )}
            </span>
          </div>

          {deliveryEvent ? (
            <div className="flex w-full items-center justify-between">
              <span className="text-sm text-foreground-500">
                <Trans>Dostavljeno:</Trans>
              </span>
              <span className="text-sm font-medium">
                {new Date(deliveryEvent.timestamp).toLocaleString(i18n.locale, {
                  timeZone: getLocalTimeZone(),
                })}
              </span>
            </div>
          ) : null}

          <div className="mt-3">
            <span className="mb-2 block text-sm text-foreground-500">
              Content:
            </span>
            <div className="min-h-[150px] overflow-auto rounded-medium border border-default-200 bg-content2 p-4">
              {jsxContent}
            </div>
          </div>
        </div>
      </div>
    );
  })();

  return (
    <div className="flex flex-col gap-4">
      {startContent}
      {data.notificationLog.fallbackNotificationActivity.map((activity) => {
        return (
          <div className="relative z-10 rounded-medium border border-default-200 bg-content1 p-4">
            <div className="absolute -left-4 top-1/2 flex h-8 w-8 -translate-y-1/2 transform items-center justify-center rounded-full border-2 border-default-200 bg-background p-0.5">
              {channelIcons[activity.channel]}
            </div>

            <div className="mb-2 ml-6 flex items-center gap-3">
              {/* Moved timestamp to more visible position */}
              <div className="flex-1">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <span className="font-medium capitalize">
                      {activity.channel}
                    </span>
                    <span className="rounded-full bg-content2 px-2 py-0.5 text-sm text-foreground-500">
                      {new Date(activity.timestamp).toLocaleString(
                        i18n.locale,
                        {
                          timeZone: getLocalTimeZone(),
                        },
                      )}
                    </span>
                  </div>
                  <div className="flex items-center gap-2 capitalize">
                    {statusIcons(activity.deliveryStatus, activity.channel)}
                    {activity.deliveryStatus}
                  </div>
                </div>
              </div>
            </div>
            <div className="ml-6 flex flex-col text-sm">
              <span className="text-foreground-500">
                {activity.statusDescription}
              </span>
              <span className="text-foreground-500">
                {activity.errorDescription}
              </span>
            </div>
          </div>
        );
      })}

      {data.notificationLog.emailNotificationActivity.map((activity) => (
        <div className="relative z-10 rounded-medium border border-default-200 bg-content1 p-4">
          <div className="absolute -left-4 top-1/2 flex h-8 w-8 -translate-y-1/2 transform items-center justify-center rounded-full border-2 border-default-200 bg-background p-0.5">
            {channelIcons["email"]}
          </div>

          <div className="mb-2 ml-6 flex items-center gap-3">
            {/* Moved timestamp to more visible position */}
            <div className="flex-1">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <span className="font-medium capitalize">Email</span>
                  <span className="rounded-full bg-content2 px-2 py-0.5 text-sm text-foreground-500">
                    {new Date(activity.timestamp).toLocaleString(i18n.locale, {
                      timeZone: getLocalTimeZone(),
                    })}
                  </span>
                </div>
                <div className="flex items-center gap-2 capitalize">
                  {emailStatusIcons(activity.eventType)}
                  {activity.eventType}
                </div>
              </div>
            </div>
          </div>
          <div className="ml-6 flex flex-col text-sm">
            {activity.errorMessage ? (
              <span className="text-foreground-500">
                {activity.errorMessage}
              </span>
            ) : null}
          </div>
        </div>
      ))}

      <span className="mt-4 text-xs text-gray-500">
        <Trans>ID obvestila:</Trans>{" "}
        <Snippet hideSymbol size="sm">
          {data.notificationLog.requestId}
        </Snippet>
      </span>
    </div>
  );
};
